import React, { useState, useRef, useMemo, useEffect, useContext } from 'react'
import ConfigurationContext from '../context/Configuration'
import { selectMatchInputSx } from '../components/Dropdown'
import AddPhotoAlternateOutlined from '@mui/icons-material/AddPhotoAlternateOutlined'
import { validateFunc } from '../constraints/constraints'
import { withTranslation, useTranslation } from 'react-i18next'
import Header from '../components/Headers/Header'
import { useQuery, useMutation, gql } from '@apollo/client'
import {
  getRestaurantProfile,
  editRestaurant,
  getCuisines,
  getActiveShopTypes,
  getCountriesList
} from '../apollo'
import ConfigurableValues from '../config/constants'
import useStyles from '../components/Restaurant/styles'
import useGlobalStyles from '../utils/globalStyles'
import {
  Box,
  Alert,
  Typography,
  Button,
  Input,
  Grid,
  Checkbox,
  Select,
  OutlinedInput,
  MenuItem,
  ListItemText,
  Switch
} from '@mui/material'
import { Container } from '@mui/system'
import LockOutlined from '@mui/icons-material/LockOutlined'
import StorefrontOutlined from '@mui/icons-material/StorefrontOutlined'
import LocalShippingOutlined from '@mui/icons-material/LocalShippingOutlined'
import AdminPanelSettingsOutlined from '@mui/icons-material/AdminPanelSettingsOutlined'
import PhotoCameraRounded from '@mui/icons-material/PhotoCameraRounded'
import PlaceOutlined from '@mui/icons-material/PlaceOutlined'
import { SectionCard } from '../components/UI/Depth'
import depth, { cardSx } from '../utils/depth'

const HeroChip = ({ children }) => (
  <Box
    component="span"
    sx={{
      px: 1.25,
      py: 0.4,
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 600,
      bgcolor: 'rgba(255,255,255,0.2)',
      border: '1px solid rgba(255,255,255,0.35)',
      backdropFilter: 'blur(4px)'
    }}>
    {children}
  </Box>
)
import CustomLoader from '../components/Loader/CustomLoader'
import InputAdornment from '@mui/material/InputAdornment'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { SHOP_TYPE } from '../utils/enums'
import StoreTypeField from '../components/Restaurant/StoreTypeField'

// None of the money or time figures on this form can sensibly be negative, and
// a type="number" box accepts a typed minus sign without complaint -- a -7
// service fee would then be subtracted from the customer's bill. Refuse the
// character as it is typed, and clamp anything pasted in on the way out.
const blockNegativeKeys = event => {
  if (['-', '+', 'e', 'E'].includes(event.key)) event.preventDefault()
}

const clampNegative = event => {
  const value = event.target.value
  if (value === '') return
  const numeric = Number(value)
  event.target.value = Number.isNaN(numeric) ? '' : String(Math.max(0, numeric))
}

// Spread onto every numeric Input on this form.
const positiveNumberProps = { min: 0, onKeyDown: blockNegativeKeys }

// Unit shown inside a field (min, days, %, the currency) so a bare number is
// never ambiguous.
const unitAdornment = (unit, position) =>
  unit ? (
    <InputAdornment position={position}>
      <Typography
        component="span"
        sx={{ fontSize: 13, fontWeight: 600, color: 'text.secondary' }}>
        {unit}
      </Typography>
    </InputAdornment>
  ) : null

const toPositiveNumber = value => Math.max(0, Number(value) || 0)

const GET_PROFILE = gql`
  ${getRestaurantProfile}
`
const EDIT_RESTAURANT = gql`
  ${editRestaurant}
`
const CUISINES = gql`
  ${getCuisines}
`
const ACTIVE_SHOP_TYPES = gql`
  ${getActiveShopTypes}
`
const GET_COUNTRIES = gql`
  ${getCountriesList}
`

const ITEM_HEIGHT = 48
const ITEM_PADDING_TOP = 8
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250
    }
  }
}

// Matches the cap the API enforces on promotional cards
const PROMO_MESSAGE_LIMIT = 5

const VendorProfile = () => {
  const { CLOUDINARY_UPLOAD_URL, CLOUDINARY_FOOD } = ConfigurableValues()

  const { t } = useTranslation()
  const configuration = useContext(ConfigurationContext)
  const currencyUnit =
    configuration?.currencySymbol || configuration?.currency || ''

  const userType = localStorage.getItem('user-borku')
    ? JSON.parse(localStorage.getItem('user-borku')).userType
    : null

  const restaurantId = localStorage.getItem('restaurantId')
  const [showPassword, setShowPassword] = useState(false)
  const [imgUrl, setImgUrl] = useState('')
  const [nameError, setNameError] = useState(null)
  const [usernameError, setUsernameError] = useState(null)
  const [passwordError, setPasswordError] = useState(null)
  const [addressError, setAddressError] = useState(null)
  const [prefixError, setPrefixError] = useState(null)
  const [deliveryTimeError, setDeliveryTimeError] = useState(null)
  const [phoneError, setPhoneError] = useState(null)
  const [webURLError, setWebURLError] = useState(null)
  const [bagFeeError, setBagFeeError] = useState(null)
  const [minimumOrderError, setMinimumOrderError] = useState(null)
  const [salesTaxError, setSalesTaxError] = useState(null)
  const [imageError, setImageError] = useState(null)
  const [errors, setErrors] = useState('')
  const [success, setSuccess] = useState('')
  const [restaurantCuisines, setRestaurantCuisines] = useState([])
  const [hygieneRatingError, setHygieneRatingError] = useState(null)
  const [selectedShopCategory, setSelectedShopCategory] = useState(
    SHOP_TYPE.RESTAURANT
  )
  const [shopSpecificType, setShopSpecificType] = useState('')
  // 'inherit' | 'row' | 'grid' - overrides the shop type's store page layout
  const [displayVariant, setDisplayVariant] = useState('inherit')
  // Promotional cards shown at the top of the store page in the apps
  const [tagline, setTagline] = useState('')
  const [promoMessages, setPromoMessages] = useState([''])
  const [promoEnabled, setPromoEnabled] = useState(false)
  // Store still being set up: listed in the apps behind a Coming Soon banner
  const [comingSoon, setComingSoon] = useState(false)
  // 'instant' quotes a delivery time in minutes, 'scheduled' quotes whole days
  const [deliveryType, setDeliveryType] = useState('instant')
  const [deliveryDays, setDeliveryDays] = useState('')
  // Country-wide store: sells across every zone of its country, cross-zone
  // orders are handled by an external courier instead of our riders.
  const [countryWideDelivery, setCountryWideDelivery] = useState(false)
  const [courierDeliveryTime, setCourierDeliveryTime] = useState('')
  const [courierDeliveryUnit, setCourierDeliveryUnit] = useState('days')
  const [country, setCountry] = useState('')

  const [allowKlarna, setAllowKlarna] = useState(true)
  const [clearPayEnabled, setClearPayEnabled] = useState(false)
  // The service fee is either a percentage of the order or a flat amount. In
  // percentage mode the floor and ceiling below keep it inside a sane range.
  const [serviceFeeType, setServiceFeeType] = useState('PERCENTAGE')
  const [serviceFeeMin, setServiceFeeMin] = useState('')
  const [serviceFeeMax, setServiceFeeMax] = useState('')

  const onCompleted = data => {
    setNameError(null)
    setAddressError(null)
    setPrefixError(null)
    setUsernameError(null)
    setPasswordError(null)
    setDeliveryTimeError(null)
    setMinimumOrderError(null)
    setSalesTaxError(null)
    setPhoneError(null)
    setWebURLError(null)
    setBagFeeError(null)
    setImageError(null)
    setHygieneRatingError(null) // Add this line
    setErrors('')
    setSuccess(t('RestaurantUpdatedSuccessfully'))
    setTimeout(hideAlert, 5000)
  }

  const onError = ({ graphQLErrors, networkError }) => {
    setNameError(null)
    setAddressError(null)
    setPrefixError(null)
    setUsernameError(null)
    setPasswordError(null)
    setDeliveryTimeError(null)
    setMinimumOrderError(null)
    setSalesTaxError(null)
    setPhoneError(null)
    setWebURLError(null)
    setBagFeeError(null)
    setImageError(null)
    setHygieneRatingError(null) // Add this line
    setSuccess('')
    if (graphQLErrors) {
      setErrors(graphQLErrors[0].message)
    }
    if (networkError) {
      setErrors(networkError.result.errors[0].message)
    }
    setTimeout(hideAlert, 5000)
  }
  const hideAlert = () => {
    setErrors('')
    setSuccess('')
  }
  const { data, error: errorQuery, loading: loadingQuery } = useQuery(
    GET_PROFILE,
    {
      variables: { id: restaurantId }
    }
  )
  console.log('Rest data: ', data)
  const [mutate, { loading }] = useMutation(EDIT_RESTAURANT, {
    onError,
    onCompleted,
    refetchQueries: [GET_PROFILE]
  })

  const formRef = useRef(null)

  const selectImage = (event, state) => {
    const result = filterImage(event)
    if (result) imageToBase64(result)
  }

  const filterImage = event => {
    let images = []
    for (var i = 0; i < event.target.files.length; i++) {
      images[i] = event.target.files.item(i)
    }
    images = images.filter(image => image.name.match(/\.(jpg|jpeg|png|gif)$/))
    return images.length ? images[0] : undefined
  }
  const imageToBase64 = imgUrl => {
    const fileReader = new FileReader()
    fileReader.onloadend = () => {
      setImgUrl(fileReader.result)
    }
    fileReader.readAsDataURL(imgUrl)
  }
  const uploadImageToCloudinary = async () => {
    if (imgUrl === '') return imgUrl

    const apiUrl = `${CLOUDINARY_UPLOAD_URL}/image/upload`
    const data = {
      file: imgUrl,
      upload_preset: CLOUDINARY_FOOD
    }
    try {
      const result = await fetch(apiUrl, {
        body: JSON.stringify(data),
        headers: {
          'content-type': 'application/json'
        },
        method: 'POST'
      })
      const imageData = await result.json()
      return imageData.secure_url
    } catch (e) {
      console.log(e)
    }
  }

  // const onSubmitValidaiton = data => {
  //   const form = formRef.current
  //   const name = form.name.value
  //   const address = form.address.value
  //   const username = form.username.value
  //   const password = form.password.value
  //   // IMPORTANT!!!!
  //   const prefix = form.prefix.value
  //   const deliveryTime = form.deliveryTime.value
  //   const minimumOrder = form.minimumOrder.value
  //   const salesTax = +form.salesTax.value
  //   const hygieneRating = form.hygieneRating.value // Add this line

  //   const nameErrors = !validateFunc({ name }, 'name')
  //   const addressErrors = !validateFunc({ address }, 'address')
  //   const prefixErrors = !validateFunc({ prefix: prefix }, 'prefix')
  //   const deliveryTimeErrors = !validateFunc(
  //     { deliveryTime: deliveryTime },
  //     'deliveryTime'
  //   )
  //   const minimumOrderErrors = !validateFunc(
  //     { minimumOrder: minimumOrder },
  //     'minimumOrder'
  //   )
  //   const usernameErrors = !validateFunc({ name: username }, 'name')
  //   const passwordErrors = !validateFunc({ password }, 'password')
  //   const salesTaxError = !validateFunc({ salesTax }, 'salesTax')
  //   const hygieneRatingErrors =
  //     hygieneRating === '' || validateFunc({ hygieneRating }, 'url')
  //   setNameError(nameErrors)
  //   setAddressError(addressErrors)
  //   setPrefixError(prefixErrors)
  //   setUsernameError(usernameErrors)
  //   setPasswordError(passwordErrors)
  //   setDeliveryTimeError(deliveryTimeErrors)
  //   setMinimumOrderError(minimumOrderErrors)
  //   setSalesTaxError(salesTaxError)
  //   setHygieneRatingError(hygieneRatingErrors) // Add this line
  //   if (
  //     !(
  //       (
  //         nameErrors &&
  //         addressErrors &&
  //         prefixErrors &&
  //         usernameErrors &&
  //         passwordErrors &&
  //         deliveryTimeErrors &&
  //         minimumOrderErrors &&
  //         salesTaxError &&
  //         hygieneRatingErrors
  //       ) // Add this line
  //     )
  //   ) {
  //     setErrors(t('FieldsRequired'))
  //   }
  //   return (
  //     nameErrors &&
  //     addressErrors &&
  //     prefixErrors &&
  //     usernameErrors &&
  //     passwordErrors &&
  //     deliveryTimeErrors &&
  //     minimumOrderErrors &&
  //     salesTaxError &&
  //     hygieneRatingErrors // Add this line
  //   )
  // }

  const onSubmitValidaiton = data => {
    const form = formRef.current
    if (!form) return false

    const name = form.name?.value || ''
    const address = form.address?.value || ''
    const username = form.username?.value || ''
    const password = form.password?.value || ''
    const prefix = form.prefix?.value || ''
    const deliveryTime = form.deliveryTime?.value || 0
    const hygieneRating = form.hygieneRating?.value || ''
    const phone = form.phone?.value || ''
    const webURL = form.webURL?.value || ''
    const minimumOrder = form.minimumOrder?.value || 0
    const salesTax = form.salesTax?.value ? +form.salesTax.value : 0

    const nameErrors = !validateFunc({ name }, 'name')
    const addressErrors = !validateFunc({ address }, 'address')
    const prefixErrors =
      userType == 'ADMIN' && prefix
        ? !validateFunc({ prefix: prefix }, 'prefix')
        : true
    const deliveryTimeErrors = !validateFunc(
      { deliveryTime: deliveryTime },
      'deliveryTime'
    )
    const usernameErrors = !validateFunc({ name: username }, 'name')
    const passwordErrors = !validateFunc({ password }, 'password')
    const phoneErrors = true // Optional field
    const webURLErrors = true // Optional field
    const bagFeeErrors = true // Optional field
    const minimumOrderErrors = !validateFunc({ minimumOrder }, 'minimumOrder')
    // Only admins see the service fee box, so vendors can never fail it
    const salesTaxErrors =
      userType == 'ADMIN' ? !validateFunc({ salesTax }, 'salesTax') : true

    // ✅ HYGIENE RATING VALIDATION: Manual validation for optional URL field
    let hygieneRatingErrors = true // Default to valid (optional field)
    if (hygieneRating && hygieneRating.trim() !== '') {
      // Only validate if not empty - check if it's a valid URL
      const urlPattern = /^https?:\/\/.+\..+/
      hygieneRatingErrors = urlPattern.test(hygieneRating.trim())
      console.log('🔍 Hygiene Rating Validation:', {
        value: hygieneRating.trim(),
        isValid: hygieneRatingErrors
      })
    }

    console.log('🔍 All Validation Results:', {
      nameErrors,
      addressErrors,
      prefixErrors,
      usernameErrors,
      passwordErrors,
      deliveryTimeErrors,
      hygieneRatingErrors,
      phoneErrors,
      webURLErrors
    })

    setNameError(nameErrors)
    setAddressError(addressErrors)
    setPrefixError(prefixErrors)
    setUsernameError(usernameErrors)
    setPasswordError(passwordErrors)
    setDeliveryTimeError(deliveryTimeErrors)
    setHygieneRatingError(hygieneRatingErrors)
    setPhoneError(phoneErrors)
    setWebURLError(webURLErrors)
    setBagFeeError(bagFeeErrors)
    setMinimumOrderError(minimumOrderErrors)
    setSalesTaxError(salesTaxErrors)

    const allFieldsValid =
      nameErrors &&
      addressErrors &&
      prefixErrors &&
      usernameErrors &&
      passwordErrors &&
      deliveryTimeErrors &&
      hygieneRatingErrors &&
      phoneErrors &&
      webURLErrors &&
      bagFeeErrors &&
      minimumOrderErrors &&
      salesTaxErrors

    console.log('🔍 Form Submission Ready:', allFieldsValid)

    if (!allFieldsValid) {
      setErrors(t('FieldsRequired'))
    }

    return allFieldsValid
  }
  const { data: cuisines } = useQuery(CUISINES)
  // The tiles the customer app shows on its home screen. The store is filed
  // under whichever one is picked here.
  const { data: shopTypesData } = useQuery(ACTIVE_SHOP_TYPES)
  const { data: countriesData } = useQuery(GET_COUNTRIES)
  const activeShopTypes = useMemo(() => shopTypesData?.activeShopTypes ?? [], [
    shopTypesData
  ])
  const cuisinesInDropdown = useMemo(
    () => cuisines?.cuisines?.map(item => item.name),
    [cuisines]
  )
  const handleCuisineChange = event => {
    const {
      target: { value }
    } = event
    setRestaurantCuisines(typeof value === 'string' ? value.split(',') : value)
  }

  useEffect(() => {
    setRestaurantCuisines(data?.restaurant?.cuisines)
    setAllowKlarna(data?.restaurant?.allowKlarna)
    setClearPayEnabled(data?.restaurant?.clearPayEnabled)
    const legacyTile = ['restaurant', 'grocery'].includes(
      data?.restaurant?.shopType
    )
      ? ''
      : data?.restaurant?.shopType
    setShopSpecificType(data?.restaurant?.categoryType || legacyTile || '')
    setDisplayVariant(data?.restaurant?.displayVariant || 'inherit')
    setTagline(data?.restaurant?.tagline || '')
    // Always keep one empty row so there is something to type into
    setPromoMessages(
      data?.restaurant?.promoMessages?.length
        ? [...data.restaurant.promoMessages]
        : ['']
    )
    setPromoEnabled(Boolean(data?.restaurant?.promoEnabled))
    setComingSoon(Boolean(data?.restaurant?.comingSoon))
    setDeliveryType(
      data?.restaurant?.deliveryType === 'scheduled' ? 'scheduled' : 'instant'
    )
    setDeliveryDays(
      data?.restaurant?.deliveryDays ? String(data.restaurant.deliveryDays) : ''
    )
    setCountryWideDelivery(Boolean(data?.restaurant?.countryWideDelivery))
    setCourierDeliveryTime(
      data?.restaurant?.courierDeliveryTime
        ? String(data.restaurant.courierDeliveryTime)
        : ''
    )
    setCourierDeliveryUnit(data?.restaurant?.courierDeliveryUnit || 'days')
    setCountry(data?.restaurant?.country || '')
    setServiceFeeType(
      data?.restaurant?.serviceFeeType === 'FIXED' ? 'FIXED' : 'PERCENTAGE'
    )
    setServiceFeeMin(
      data?.restaurant?.serviceFeeMin != null
        ? String(data.restaurant.serviceFeeMin)
        : ''
    )
    setServiceFeeMax(
      data?.restaurant?.serviceFeeMax != null
        ? String(data.restaurant.serviceFeeMax)
        : ''
    )
    // Stores filed straight under a tile ('fashion-beauty') before Store Type
    // existed are stores; their tile moves into Store Type below
    if (data?.restaurant?.shopType) {
      setSelectedShopCategory(
        data.restaurant.shopType === SHOP_TYPE.RESTAURANT
          ? SHOP_TYPE.RESTAURANT
          : SHOP_TYPE.STORE
      )
    }
  }, [
    data?.restaurant?.cuisines,
    data?.restaurant?.allowKlarna,
    data?.restaurant?.clearPayEnabled,
    data?.restaurant?.shopType,
    data?.restaurant?.displayVariant,
    data?.restaurant?.tagline,
    data?.restaurant?.promoMessages,
    data?.restaurant?.promoEnabled,
    data?.restaurant?.comingSoon,
    data?.restaurant?.deliveryType,
    data?.restaurant?.deliveryDays,
    data?.restaurant?.countryWideDelivery,
    data?.restaurant?.courierDeliveryTime,
    data?.restaurant?.courierDeliveryUnit,
    data?.restaurant?.country,
    data?.restaurant?.serviceFeeType,
    data?.restaurant?.serviceFeeMin,
    data?.restaurant?.serviceFeeMax
  ])

  const classes = useStyles()
  const globalClasses = useGlobalStyles()
  return (
    <>
      <Header />
      <Container className={globalClasses.flex} fluid>
        {errorQuery && <span>{errorQuery.message}</span>}
        {loadingQuery ? (
          <CustomLoader />
        ) : (
          <Box>
            <Box
              sx={{
                position: 'relative',
                overflow: 'hidden',
                borderRadius: '22px',
                mb: 3,
                p: { xs: 2.5, sm: 3.5 },
                color: 'common.white',
                background: depth.brandGradient,
                boxShadow: depth.brandShadow,
                display: 'flex',
                alignItems: 'center',
                gap: { xs: 2, sm: 3 },
                flexWrap: 'wrap',
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  right: -80,
                  top: -120,
                  width: 320,
                  height: 320,
                  borderRadius: '50%',
                  background: 'rgba(255,255,255,0.12)'
                }
              }}>
              <Box sx={{ position: 'relative', flexShrink: 0, zIndex: 1 }}>
                {imgUrl || data?.restaurant?.image ? (
                  <Box
                    component="img"
                    alt={t('Image')}
                    src={imgUrl || data?.restaurant?.image}
                    sx={{
                      width: { xs: 84, sm: 104 },
                      height: { xs: 84, sm: 104 },
                      borderRadius: '24px',
                      objectFit: 'cover',
                      bgcolor: 'common.white',
                      border: '4px solid rgba(255,255,255,0.9)',
                      boxShadow: '0 14px 28px -10px rgba(0,0,0,0.45)',
                      outline:
                        imageError === false ? '2px solid #d32f2f' : 'none'
                    }}
                  />
                ) : (
                  // No logo yet: a clear empty tile instead of the browser's
                  // broken-image glyph.
                  <Box
                    sx={{
                      width: { xs: 84, sm: 104 },
                      height: { xs: 84, sm: 104 },
                      borderRadius: '24px',
                      bgcolor: 'rgba(255,255,255,0.18)',
                      border: '2px dashed rgba(255,255,255,0.75)',
                      outline:
                        imageError === false ? '2px solid #d32f2f' : 'none',
                      color: 'common.white',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5
                    }}>
                    <AddPhotoAlternateOutlined sx={{ fontSize: 30 }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700 }}>
                      {t('NoImageYet')}
                    </Typography>
                  </Box>
                )}
                <Box
                  component="label"
                  htmlFor="file-upload"
                  title={t('UploadAnImage')}
                  sx={{
                    position: 'absolute',
                    right: -8,
                    bottom: -8,
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: 'common.white',
                    color: 'primary.main',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    boxShadow: '0 6px 14px -4px rgba(0,0,0,0.35)'
                  }}>
                  <PhotoCameraRounded sx={{ fontSize: 18 }} />
                </Box>
                <input
                  className={classes.file}
                  id="file-upload"
                  type="file"
                  accept="image/*"
                  onChange={event => {
                    selectImage(event, 'image_url')
                    setImageError(true)
                  }}
                />
              </Box>
              <Box sx={{ minWidth: 0, flexGrow: 1, zIndex: 1 }}>
                <Typography
                  sx={{
                    fontSize: { xs: 22, sm: 28 },
                    fontWeight: 800,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.15
                  }}>
                  {data?.restaurant?.name || t('UpdateProfile')}
                </Typography>
                {data?.restaurant?.address ? (
                  <Typography
                    sx={{
                      mt: 0.75,
                      fontSize: 14,
                      opacity: 0.9,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 0.5
                    }}>
                    <PlaceOutlined sx={{ fontSize: 16 }} />
                    {data.restaurant.address}
                  </Typography>
                ) : null}
                <Box
                  sx={{ display: 'flex', gap: 1, mt: 1.5, flexWrap: 'wrap' }}>
                  <HeroChip>
                    {deliveryType === 'scheduled'
                      ? t('DeliveryTypeScheduled')
                      : t('DeliveryTypeInstant')}
                  </HeroChip>
                  {countryWideDelivery ? (
                    <HeroChip>{t('CountryWideDelivery')}</HeroChip>
                  ) : null}
                  {data?.restaurant?.orderPrefix ? (
                    <HeroChip>#{data.restaurant.orderPrefix}</HeroChip>
                  ) : null}
                </Box>
              </Box>
              <Box
                sx={{
                  zIndex: 1,
                  '& label': { color: 'common.white', fontSize: 13 },
                  '& .MuiSwitch-root': { color: 'common.white' }
                }}>
                {userType == 'ADMIN' && (
                  <Box
                    display="flex"
                    alignItems="center"
                    gap={2}
                    flexWrap="wrap">
                    <Box display="flex" alignItems="center" gap={1}>
                      <label style={{ whiteSpace: 'nowrap' }}>
                        Allow Klarna
                      </label>
                      <Switch
                        checked={allowKlarna}
                        onChange={event => {
                          setAllowKlarna(event.target.checked)
                        }}
                        color="default"
                      />
                    </Box>
                    <Box display="flex" alignItems="center" gap={1}>
                      <label style={{ whiteSpace: 'nowrap' }}>
                        ClearPay Enabled
                      </label>
                      <Switch
                        checked={clearPayEnabled}
                        onChange={event => {
                          setClearPayEnabled(event.target.checked)
                        }}
                        color="default"
                      />
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>
            <form ref={formRef}>
              <SectionCard
                icon={LockOutlined}
                tone="slate"
                title={t('AccountAccess')}
                subtitle={t('ProfileAccountHelp')}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography className={classes.labelText}>
                        {t('RestaurantUsername')}
                      </Typography>
                      <Input
                        style={{ marginTop: -1 }}
                        name="username"
                        id="input-type-username"
                        placeholder={t('RestaurantUsername')}
                        type="text"
                        defaultValue={
                          (data && data?.restaurant?.username) || ''
                        }
                        disableUnderline
                        className={[
                          globalClasses.input,
                          usernameError === false
                            ? globalClasses.inputError
                            : usernameError === true
                            ? globalClasses.inputSuccess
                            : ''
                        ]}
                        onChange={event => {
                          if (event.target.value.includes(' ')) {
                            const usernameWithoutSpaces = event.target.value.replace(
                              / /g,
                              ''
                            )
                            event.target.value = usernameWithoutSpaces
                          }
                        }}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography className={classes.labelText}>
                        {t('Password')}
                      </Typography>
                      <Input
                        style={{ marginTop: -1 }}
                        name="password"
                        id="input-type-password"
                        placeholder={t('PHRestaurantPassword')}
                        type={showPassword ? 'text' : 'password'}
                        defaultValue={
                          (data && data?.restaurant?.password) || ''
                        }
                        disableUnderline
                        className={[
                          globalClasses.input,
                          passwordError === false
                            ? globalClasses.inputError
                            : passwordError === true
                            ? globalClasses.inputSuccess
                            : ''
                        ]}
                        endAdornment={
                          <InputAdornment position="end">
                            <Checkbox
                              checked={showPassword}
                              onChange={() => setShowPassword(!showPassword)}
                              color="primary"
                              icon={<VisibilityOffIcon />}
                              checkedIcon={<VisibilityIcon />}
                            />
                          </InputAdornment>
                        }
                      />
                    </Box>
                  </Grid>
                </Grid>
              </SectionCard>
              <SectionCard
                icon={StorefrontOutlined}
                tone="orange"
                title={t('StoreDetails')}
                subtitle={t('ProfileStoreDetailsHelp')}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography className={classes.labelText}>
                        {t('Name')}
                      </Typography>
                      <Input
                        style={{ marginTop: -1 }}
                        name="name"
                        id="input-type-name"
                        placeholder={t('PHRestaurantName')}
                        type="text"
                        defaultValue={(data && data?.restaurant?.name) || ''}
                        disableUnderline
                        className={[
                          globalClasses.input,
                          nameError === false
                            ? globalClasses.inputError
                            : nameError === true
                            ? globalClasses.inputSuccess
                            : ''
                        ]}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography className={classes.labelText}>
                        {t('Address')}
                      </Typography>
                      <Input
                        style={{ marginTop: -1 }}
                        name="address"
                        id="input-type-address"
                        placeholder={t('PHRestaurantAddress')}
                        type="text"
                        defaultValue={(data && data?.restaurant?.address) || ''}
                        disableUnderline
                        className={[
                          globalClasses.input,
                          addressError === false
                            ? globalClasses.inputError
                            : addressError === true
                            ? globalClasses.inputSuccess
                            : ''
                        ]}
                      />
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography className={classes.labelText}>
                        {t('Phone')}
                      </Typography>
                      <Input
                        style={{ marginTop: -1 }}
                        name="phone"
                        id="input-type-phone"
                        placeholder={t('RestaurantPhone')}
                        type="tel"
                        defaultValue={(data && data?.restaurant?.phone) || ''}
                        disableUnderline
                        className={[
                          globalClasses.input,
                          phoneError === false
                            ? globalClasses.inputError
                            : phoneError === true
                            ? globalClasses.inputSuccess
                            : ''
                        ]}
                      />
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography className={classes.labelText}>
                        {t('Website URL')}
                      </Typography>
                      <Input
                        style={{ marginTop: -1 }}
                        name="webURL"
                        id="input-type-weburl"
                        placeholder={t('RestaurantWebURL')}
                        type="text"
                        defaultValue={(data && data?.restaurant?.webURL) || ''}
                        disableUnderline
                        className={[
                          globalClasses.input,
                          webURLError === false
                            ? globalClasses.inputError
                            : webURLError === true
                            ? globalClasses.inputSuccess
                            : ''
                        ]}
                      />
                    </Box>
                  </Grid>
                </Grid>
              </SectionCard>
              <SectionCard
                icon={LocalShippingOutlined}
                tone="blue"
                title={t('DeliveryAndPricing')}
                subtitle={t('ProfileDeliveryHelp')}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography className={classes.labelText}>
                        {t('Bag Fee')}
                      </Typography>
                      <Input
                        style={{ marginTop: -1 }}
                        name="bagFee"
                        id="input-type-bagfee"
                        placeholder={t('RestaurantBagFee')}
                        type="number"
                        inputProps={positiveNumberProps}
                        onBlur={clampNegative}
                        defaultValue={(data && data?.restaurant?.bagFee) || ''}
                        startAdornment={unitAdornment(currencyUnit, 'start')}
                        disableUnderline
                        className={[
                          globalClasses.input,
                          bagFeeError === false
                            ? globalClasses.inputError
                            : bagFeeError === true
                            ? globalClasses.inputSuccess
                            : ''
                        ]}
                      />
                      <Typography className={classes.helperText}>
                        {t('BagFeeHelp')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography className={classes.labelText}>
                        {t('MinOrder')}
                      </Typography>
                      <Input
                        style={{ marginTop: -1 }}
                        name="minimumOrder"
                        id="input-type-minimum-order"
                        placeholder={t('MinOrder')}
                        type="number"
                        inputProps={positiveNumberProps}
                        onBlur={clampNegative}
                        startAdornment={unitAdornment(currencyUnit, 'start')}
                        disableUnderline
                        defaultValue={data && data?.restaurant?.minimumOrder}
                        className={[
                          globalClasses.input,
                          minimumOrderError === false
                            ? globalClasses.inputError
                            : minimumOrderError === true
                            ? globalClasses.inputSuccess
                            : ''
                        ]}
                      />
                      <Typography className={classes.helperText}>
                        {t('MinOrderHelp')}
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box>
                      <Typography className={classes.labelText}>
                        {t('DeliveryType')}
                      </Typography>
                      <Select
                        sx={selectMatchInputSx}
                        id="delivery-type"
                        name="deliveryType"
                        value={deliveryType}
                        onChange={event => setDeliveryType(event.target.value)}
                        displayEmpty
                        className={globalClasses.input}>
                        <MenuItem value="instant" color="default">
                          {t('DeliveryTypeInstant')}
                        </MenuItem>
                        <MenuItem value="scheduled" color="default">
                          {t('DeliveryTypeScheduled')}
                        </MenuItem>
                      </Select>
                      <Typography className={classes.helperText}>
                        {t('DeliveryTypeHelp')}
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {/* Instant stores quote minutes, scheduled ones quote days */}
                    <Box hidden={deliveryType !== 'instant'}>
                      <Typography className={classes.labelText}>
                        {t('DeliveryTime')}
                      </Typography>
                      <Input
                        style={{ marginTop: -1 }}
                        name="deliveryTime"
                        id="input-type-delivery-time"
                        placeholder={t('DeliveryTime')}
                        type="number"
                        inputProps={positiveNumberProps}
                        onBlur={clampNegative}
                        defaultValue={data && data?.restaurant?.deliveryTime}
                        endAdornment={unitAdornment(t('UnitMinutes'), 'end')}
                        disableUnderline
                        className={[
                          globalClasses.input,
                          deliveryTimeError === false
                            ? globalClasses.inputError
                            : deliveryTimeError === true
                            ? globalClasses.inputSuccess
                            : ''
                        ]}
                      />
                      <Typography className={classes.helperText}>
                        {t('DeliveryTimeHelp')}
                      </Typography>
                    </Box>
                    {deliveryType === 'scheduled' ? (
                      <Box>
                        <Typography className={classes.labelText}>
                          {t('DeliveryDays')}
                        </Typography>
                        <Input
                          style={{ marginTop: -1 }}
                          id="input-type-delivery-days"
                          placeholder={t('PHDeliveryDays')}
                          type="number"
                          inputProps={{ ...positiveNumberProps, min: 1 }}
                          value={deliveryDays}
                          onChange={event =>
                            setDeliveryDays(event.target.value)
                          }
                          endAdornment={unitAdornment(t('UnitDays'), 'end')}
                          disableUnderline
                          className={[globalClasses.input]}
                        />
                        <Typography className={classes.helperText}>
                          {t('DeliveryDaysHelp')}
                        </Typography>
                      </Box>
                    ) : null}
                  </Grid>
                </Grid>

                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Box
                      style={{ display: 'flex', alignItems: 'center' }}
                      mt={2}>
                      <Switch
                        checked={countryWideDelivery}
                        onChange={event =>
                          setCountryWideDelivery(event.target.checked)
                        }
                      />
                      <Typography className={classes.switchLabel}>
                        {t('CountryWideDelivery')}
                      </Typography>
                    </Box>
                    <Typography className={classes.helperText}>
                      {t('CountryWideDeliveryHelp')}
                    </Typography>
                  </Grid>
                  {countryWideDelivery && (
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography className={classes.labelText}>
                          {t('Country')}
                        </Typography>
                        <Select
                          style={{ margin: 0, padding: 0 }}
                          id="restaurant-country"
                          value={country}
                          onChange={event => setCountry(event.target.value)}
                          displayEmpty
                          className={globalClasses.input}>
                          <MenuItem value="" color="default">
                            {t('SelectCountry')}
                          </MenuItem>
                          {(countriesData?.getCountries || [])
                            .filter(c => c.iso2)
                            .map(c => (
                              <MenuItem
                                key={c.iso2}
                                value={c.iso2}
                                color="default">
                                {c.name}
                              </MenuItem>
                            ))}
                        </Select>
                      </Box>
                    </Grid>
                  )}
                  {countryWideDelivery && (
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography className={classes.labelText}>
                          {t('CourierDeliveryTime')}
                        </Typography>
                        <Box style={{ display: 'flex', gap: 8 }}>
                          <Input
                            style={{ marginTop: -1, flex: 1 }}
                            id="input-type-courier-delivery-time"
                            placeholder="2"
                            type="number"
                            inputProps={{ ...positiveNumberProps, min: 1 }}
                            value={courierDeliveryTime}
                            onChange={event =>
                              setCourierDeliveryTime(event.target.value)
                            }
                            disableUnderline
                            className={globalClasses.input}
                          />
                          <Select
                            sx={{ ...selectMatchInputSx, minWidth: 130 }}
                            id="courier-delivery-unit"
                            value={courierDeliveryUnit}
                            onChange={event =>
                              setCourierDeliveryUnit(event.target.value)
                            }
                            className={globalClasses.input}>
                            <MenuItem value="minutes">
                              {t('UnitMinutesLong')}
                            </MenuItem>
                            <MenuItem value="hours">{t('UnitHours')}</MenuItem>
                            <MenuItem value="days">{t('UnitDays')}</MenuItem>
                          </Select>
                        </Box>
                        <Typography className={classes.helperText}>
                          {t('CourierDeliveryTimeHelp')}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
              {userType == 'ADMIN' && (
                <SectionCard
                  icon={AdminPanelSettingsOutlined}
                  tone="violet"
                  title={t('AdminSettings')}
                  subtitle={t('ProfileAdminHelp')}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography className={classes.labelText}>
                          {t('ServiceFeeType')}
                        </Typography>
                        <Select
                          sx={selectMatchInputSx}
                          id="service-fee-type"
                          name="serviceFeeType"
                          value={serviceFeeType}
                          onChange={event =>
                            setServiceFeeType(event.target.value)
                          }
                          displayEmpty
                          className={globalClasses.input}>
                          <MenuItem value="PERCENTAGE" color="default">
                            {t('ServiceFeePercentage')}
                          </MenuItem>
                          <MenuItem value="FIXED" color="default">
                            {t('ServiceFeeFixed')}
                          </MenuItem>
                        </Select>
                        <Typography className={classes.helperText}>
                          {serviceFeeType === 'FIXED'
                            ? t('ServiceFeeFixedHelp')
                            : t('ServiceFeePercentageHelp')}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography className={classes.labelText}>
                          {serviceFeeType === 'FIXED'
                            ? t('ServiceFeeAmount')
                            : t('ServiceFeePercent')}
                        </Typography>
                        <Input
                          style={{ marginTop: -1 }}
                          name="salesTax"
                          id="input-type-sales-tax"
                          placeholder={t('ServiceFee')}
                          type="number"
                          inputProps={positiveNumberProps}
                          onBlur={clampNegative}
                          defaultValue={data && data?.restaurant?.tax}
                          startAdornment={
                            serviceFeeType === 'FIXED'
                              ? unitAdornment(currencyUnit, 'start')
                              : null
                          }
                          endAdornment={
                            serviceFeeType === 'FIXED'
                              ? null
                              : unitAdornment('%', 'end')
                          }
                          disableUnderline
                          className={[
                            globalClasses.input,
                            salesTaxError === false
                              ? globalClasses.inputError
                              : salesTaxError === true
                              ? globalClasses.inputSuccess
                              : ''
                          ]}
                        />
                      </Box>
                      {/* A floor and ceiling only make sense on a percentage */}
                      {serviceFeeType === 'PERCENTAGE' ? (
                        <>
                          <Box>
                            <Typography className={classes.labelText}>
                              {t('ServiceFeeMin')}
                            </Typography>
                            <Input
                              style={{ marginTop: -1 }}
                              id="input-service-fee-min"
                              placeholder={t('ServiceFeeMin')}
                              type="number"
                              inputProps={{
                                ...positiveNumberProps,
                                step: 0.01
                              }}
                              value={serviceFeeMin}
                              onChange={event =>
                                setServiceFeeMin(
                                  event.target.value === ''
                                    ? ''
                                    : String(
                                        toPositiveNumber(event.target.value)
                                      )
                                )
                              }
                              startAdornment={unitAdornment(
                                currencyUnit,
                                'start'
                              )}
                              disableUnderline
                              className={globalClasses.input}
                            />
                            <Typography className={classes.helperText}>
                              {t('ServiceFeeMinHelp')}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography className={classes.labelText}>
                              {t('ServiceFeeMax')}
                            </Typography>
                            <Input
                              style={{ marginTop: -1 }}
                              id="input-service-fee-max"
                              placeholder={t('ServiceFeeMax')}
                              type="number"
                              inputProps={{
                                ...positiveNumberProps,
                                step: 0.01
                              }}
                              value={serviceFeeMax}
                              onChange={event =>
                                setServiceFeeMax(
                                  event.target.value === ''
                                    ? ''
                                    : String(
                                        toPositiveNumber(event.target.value)
                                      )
                                )
                              }
                              startAdornment={unitAdornment(
                                currencyUnit,
                                'start'
                              )}
                              disableUnderline
                              className={globalClasses.input}
                            />
                            <Typography className={classes.helperText}>
                              {t('ServiceFeeMaxHelp')}
                            </Typography>
                          </Box>
                        </>
                      ) : null}
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography className={classes.labelText}>
                          {t('OrderPrefix')}
                        </Typography>
                        <Input
                          style={{ marginTop: -1 }}
                          name="prefix"
                          id="input-type-order_id_prefix"
                          placeholder={t('OrderPrefix')}
                          type="text"
                          defaultValue={data && data?.restaurant?.orderPrefix}
                          disableUnderline
                          className={[
                            globalClasses.input,
                            prefixError === false
                              ? globalClasses.inputError
                              : prefixError === true
                              ? globalClasses.inputSuccess
                              : ''
                          ]}
                        />
                        <Typography className={classes.helperText}>
                          {t('OrderPrefixHelp')}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography className={classes.labelText}>
                          {t('Delivery Rate')}
                        </Typography>
                        <Input
                          style={{ marginTop: -1 }}
                          name="deliveryRate"
                          id="input-type-delivery-rate"
                          placeholder={t('Delivery Rate')}
                          type="number"
                          inputProps={positiveNumberProps}
                          onBlur={clampNegative}
                          defaultValue={
                            data && data?.restaurant?.restDeliveryRate
                          }
                          startAdornment={unitAdornment(currencyUnit, 'start')}
                          endAdornment={unitAdornment(t('UnitPerKm'), 'end')}
                          disableUnderline
                          className={[globalClasses.input]}
                        />
                        <Typography className={classes.helperText}>
                          {t('DeliveryRateHelp')}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography className={classes.labelText}>
                          {t('HygieneRating')}
                        </Typography>
                        <Input
                          style={{ marginTop: -1 }}
                          name="hygieneRating"
                          id="input-type-hygiene-rating"
                          placeholder={t('PHHygieneRating')}
                          type="url"
                          defaultValue={data && data?.restaurant?.hygieneRating}
                          disableUnderline
                          className={[
                            globalClasses.input,
                            hygieneRatingError === false
                              ? globalClasses.inputError
                              : hygieneRatingError === true
                              ? globalClasses.inputSuccess
                              : ''
                          ]}
                        />
                        <Typography className={classes.helperText}>
                          {t('HygieneRatingHelp')}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography className={classes.labelText}>
                          {t('Shop Category')}
                        </Typography>
                        <Select
                          sx={selectMatchInputSx}
                          id="shop-type"
                          name="shopType"
                          value={selectedShopCategory}
                          onChange={event =>
                            setSelectedShopCategory(event.target.value)
                          }
                          displayEmpty
                          className={globalClasses.input}>
                          <MenuItem value={SHOP_TYPE.RESTAURANT}>
                            {t('Restaurant')}
                          </MenuItem>
                          <MenuItem value={SHOP_TYPE.STORE}>
                            {t('Store')}
                          </MenuItem>
                        </Select>
                      </Box>
                      <Box mt={2}>
                        <StoreTypeField
                          shopTypes={activeShopTypes}
                          value={shopSpecificType}
                          onChange={setShopSpecificType}
                        />
                      </Box>
                      <Box mt={2}>
                        <Typography className={classes.labelText}>
                          {t('StorePageLayout')}
                        </Typography>
                        <Select
                          sx={selectMatchInputSx}
                          id="display-variant"
                          name="displayVariant"
                          value={displayVariant}
                          onChange={event =>
                            setDisplayVariant(event.target.value)
                          }
                          displayEmpty
                          className={globalClasses.input}>
                          <MenuItem value="inherit" color="default">
                            {t('LayoutFollowShopType')}
                          </MenuItem>
                          <MenuItem value="row" color="default">
                            {t('VariantRow')}
                          </MenuItem>
                          <MenuItem value="grid" color="default">
                            {t('VariantGrid')}
                          </MenuItem>
                        </Select>
                        <Typography className={classes.helperText}>
                          {t('StorePageLayoutHelp')}
                        </Typography>
                      </Box>
                      <Box mt={4}>
                        <Typography className={classes.labelText}>
                          {t('StoreTagline')}
                        </Typography>
                        <Input
                          style={{ marginTop: -1 }}
                          id="input-tagline"
                          name="input-tagline"
                          placeholder={t('PHStoreTagline')}
                          type="text"
                          value={tagline}
                          inputProps={{ maxLength: 80 }}
                          onChange={event => setTagline(event.target.value)}
                          disableUnderline
                          className={[globalClasses.input]}
                        />
                        <Typography className={classes.helperText}>
                          {t('StoreTaglineHelp')}
                        </Typography>
                      </Box>
                      <Box mt={4}>
                        <Typography className={classes.sectionLabel}>
                          {t('PromoMessage')}
                        </Typography>
                        {promoMessages.map((message, index) => (
                          <Box
                            key={index}
                            mb={1}
                            style={{ display: 'flex', alignItems: 'center' }}>
                            <Input
                              style={{ marginTop: -1 }}
                              id={`input-promo-message-${index}`}
                              name={`input-promo-message-${index}`}
                              placeholder={t('PHPromoMessage')}
                              type="text"
                              value={message}
                              onChange={event =>
                                setPromoMessages(current =>
                                  current.map((item, itemIndex) =>
                                    itemIndex === index
                                      ? event.target.value
                                      : item
                                  )
                                )
                              }
                              disableUnderline
                              className={[globalClasses.input]}
                            />
                            {promoMessages.length > 1 ? (
                              <Button
                                type="button"
                                onClick={() =>
                                  setPromoMessages(current =>
                                    current.filter(
                                      (item, itemIndex) => itemIndex !== index
                                    )
                                  )
                                }
                                style={{ minWidth: 32 }}>
                                ×
                              </Button>
                            ) : null}
                          </Box>
                        ))}
                        {promoMessages.length < PROMO_MESSAGE_LIMIT ? (
                          <Button
                            type="button"
                            onClick={() =>
                              setPromoMessages(current => [...current, ''])
                            }
                            style={{
                              textTransform: 'none',
                              fontSize: 13,
                              marginLeft: '4%'
                            }}>
                            + {t('AddPromoMessage')}
                          </Button>
                        ) : null}
                        <Box
                          style={{ display: 'flex', alignItems: 'center' }}
                          mt={2}>
                          <Switch
                            checked={promoEnabled}
                            onChange={event =>
                              setPromoEnabled(event.target.checked)
                            }
                          />
                          <Typography className={classes.switchLabel}>
                            {t('ShowPromoMessage')}
                          </Typography>
                        </Box>
                        <Typography className={classes.helperText}>
                          {t('PromoMessageHelp')}
                        </Typography>
                      </Box>
                      <Box mt={4}>
                        <Typography className={classes.sectionLabel}>
                          {t('OpeningSoon')}
                        </Typography>
                        <Box
                          style={{ display: 'flex', alignItems: 'center' }}
                          mt={1}>
                          <Switch
                            checked={comingSoon}
                            onChange={event =>
                              setComingSoon(event.target.checked)
                            }
                          />
                          <Typography className={classes.switchLabel}>
                            {t('ShowComingSoonBanner')}
                          </Typography>
                        </Box>
                        <Typography className={classes.helperText}>
                          {t('OpeningSoonHelp')}
                        </Typography>
                      </Box>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Box>
                        <Typography className={classes.labelText}>
                          {t('Cuisines')}
                        </Typography>
                        <Select
                          multiple
                          onChange={handleCuisineChange}
                          input={<OutlinedInput />}
                          value={restaurantCuisines}
                          renderValue={selected => selected.join(', ')}
                          defaultValue={data?.restaurant?.cuisines}
                          MenuProps={MenuProps}
                          className={[globalClasses.input]}
                          sx={selectMatchInputSx}>
                          {cuisinesInDropdown?.map(cuisine => (
                            <MenuItem
                              key={'restaurant-cuisine-' + cuisine}
                              value={cuisine}
                              style={{
                                color: '#000000',
                                textTransform: 'capitalize'
                              }}>
                              <Checkbox
                                checked={
                                  restaurantCuisines?.indexOf(cuisine) > -1
                                }
                              />
                              <ListItemText primary={cuisine} />
                            </MenuItem>
                          ))}
                        </Select>
                      </Box>
                    </Grid>
                  </Grid>
                </SectionCard>
              )}
              <Box
                sx={{
                  position: 'sticky',
                  bottom: 16,
                  zIndex: 5,
                  ...cardSx,
                  px: 3,
                  py: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                  '& .MuiButton-root': { mt: 0 }
                }}>
                <Typography sx={{ fontSize: 13, color: 'text.disabled' }}>
                  {t('ProfileSaveHint')}
                </Typography>
                <Button
                  className={globalClasses.button}
                  disabled={loading}
                  onClick={async e => {
                    e.preventDefault()
                    if (onSubmitValidaiton()) {
                      const imgUpload = await uploadImageToCloudinary()
                      const finalImage =
                        imgUpload || data?.restaurant?.image || ''
                      const form = formRef.current
                      const name = form.name.value
                      const address = form.address.value
                      const prefix =
                        form.prefix?.value ||
                        data?.restaurant?.orderPrefix ||
                        ''
                      const deliveryTime = form.deliveryTime?.value || 0
                      const username = form.username.value
                      const password = form.password.value
                      // restaurant / store; the tile lives in categoryType
                      let shopType = selectedShopCategory
                      const hygieneRating = form.hygieneRating?.value || ''
                      const phone = form.phone?.value || ''
                      const webURL = form.webURL?.value || ''
                      const bagFee = form.bagFee?.value || 0
                      const minimumOrder = form.minimumOrder?.value || 0
                      // Admin-only boxes; vendors never render them
                      const salesTax = form.salesTax?.value
                      const deliveryRate = form.deliveryRate?.value
                      if (shopType == 'store') {
                        shopType = 'grocery'
                      }
                      mutate({
                        variables: {
                          restaurantInput: {
                            _id: restaurantId,
                            name,
                            address,
                            image: finalImage,
                            orderPrefix: prefix,
                            deliveryTime: toPositiveNumber(deliveryTime),
                            deliveryType,
                            // Only meaningful for scheduled stores
                            deliveryDays:
                              deliveryType === 'scheduled'
                                ? Number(deliveryDays) || 0
                                : 0,
                            countryWideDelivery,
                            country: countryWideDelivery
                              ? country || null
                              : null,
                            courierDeliveryTime: countryWideDelivery
                              ? Number(courierDeliveryTime) || 0
                              : 0,
                            courierDeliveryUnit,
                            username: username,
                            password: password,
                            shopType,
                            cuisines:
                              restaurantCuisines ||
                              data?.restaurant?.cuisines ||
                              [],
                            allowKlarna: allowKlarna,
                            clearPayEnabled: clearPayEnabled,
                            hygieneRating: hygieneRating || '',
                            // Home screen tile + label on the store card
                            categoryType: shopSpecificType.trim(),
                            displayVariant,
                            tagline: tagline.trim(),
                            promoMessages: promoMessages
                              .map(message => message.trim())
                              .filter(Boolean),
                            promoEnabled,
                            comingSoon,
                            phone,
                            webURL,
                            bagFee: toPositiveNumber(bagFee),
                            minimumOrder: toPositiveNumber(minimumOrder),
                            // Left out entirely for vendors so the resolver
                            // keeps whatever the admin last set
                            ...(userType === 'ADMIN'
                              ? {
                                  salesTax: toPositiveNumber(salesTax),
                                  restDeliveryRate: toPositiveNumber(
                                    deliveryRate
                                  ),
                                  serviceFeeType,
                                  serviceFeeMin: toPositiveNumber(
                                    serviceFeeMin
                                  ),
                                  serviceFeeMax: toPositiveNumber(serviceFeeMax)
                                }
                              : {})
                          }
                        }
                      })
                    }
                  }}>
                  {t('Save')}
                </Button>
              </Box>
            </form>
            <Box mt={2}>
              {success && (
                <Alert
                  className={globalClasses.alertSuccess}
                  variant="filled"
                  severity="success">
                  {success}
                </Alert>
              )}
              {errors && (
                <Alert
                  className={globalClasses.alertError}
                  variant="filled"
                  severity="error">
                  {errors}
                </Alert>
              )}
            </Box>
          </Box>
        )}
      </Container>
    </>
  )
}
export default withTranslation()(VendorProfile)
